/**
 * Inline "… Read more" / "Read less" at end of clamped lines (collection split banner).
 * Collapsed view uses plain text from content; expanded restores full HTML (rte).
 */
if (!customElements.get('collection-inline-read-more')) {
  customElements.define(
    'collection-inline-read-more',
    class CollectionInlineReadMore extends HTMLElement {
      connectedCallback() {
        this._source = this.querySelector('.collection-inline-read-more__source');
        if (!this._source) {
          return;
        }
        this._originalHtml = this._source.innerHTML;
        this._lineLimit = Math.max(1, parseInt(this.dataset.lineLimit || '3', 10) || 3);
        this._showMore = this.dataset.showMore || 'Read more';
        this._showLess = this.dataset.showLess || 'Read less';
        this._expanded = false;
        this._debouncedResize = this._debounce(() => this._apply(), 150);
        this._onResize = () => this._debouncedResize();

        window.addEventListener('resize', this._onResize);
        this._boot();
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this._onResize);
      }

      _debounce(fn, ms) {
        let t;
        return function () {
          clearTimeout(t);
          t = setTimeout(fn, ms);
        };
      }

      async _boot() {
        if (document.fonts && document.fonts.ready) {
          await document.fonts.ready;
        }
        await new Promise((r) => requestAnimationFrame(r));
        this._apply();
      }

      _apply() {
        if (this._expanded) {
          this._renderExpanded();
          return;
        }
        this._renderCollapsedOrFull();
      }

      _getPlainText() {
        const tmp = document.createElement('div');
        tmp.innerHTML = this._originalHtml;
        return tmp.innerText.replace(/\s+/g, ' ').trim();
      }

      _needsCollapse(width, plain, maxLines) {
        const mirror = document.createElement('div');
        mirror.style.cssText =
          'position:absolute;left:-9999px;top:0;visibility:hidden;pointer-events:none;width:' +
          width +
          'px;';
        mirror.className = this._source.className;
        mirror.textContent = plain;
        document.body.appendChild(mirror);
        const lh = parseFloat(getComputedStyle(mirror).lineHeight) || 22;
        const maxH = lh * maxLines + 2;
        const overflow = mirror.scrollHeight > maxH;
        document.body.removeChild(mirror);
        return overflow;
      }

      _findCutIndex(width, plain, maxLines) {
        const test = document.createElement('div');
        test.style.cssText =
          'position:absolute;left:-9999px;top:0;visibility:hidden;pointer-events:none;width:' +
          width +
          'px;';
        test.className = this._source.className;
        document.body.appendChild(test);
        const lh = parseFloat(getComputedStyle(test).lineHeight) || 22;
        const maxH = lh * maxLines + 2;
        const suffix = '… ' + this._showMore;

        const fits = (len) => {
          const prefix = plain.slice(0, len);
          test.textContent = prefix + suffix;
          return test.scrollHeight <= maxH;
        };

        let lo = 0;
        let hi = plain.length;
        while (lo < hi) {
          const mid = Math.ceil((lo + hi) / 2);
          if (fits(mid)) {
            lo = mid;
          } else {
            hi = mid - 1;
          }
        }
        document.body.removeChild(test);

        let cut = lo;
        if (cut < plain.length && cut > 0) {
          const slice = plain.slice(0, cut);
          const lastSpace = Math.max(slice.lastIndexOf(' '), slice.lastIndexOf('\n'));
          if (lastSpace > cut * 0.5) {
            cut = lastSpace;
          }
        }
        return cut;
      }

      _escapeHtml(text) {
        const d = document.createElement('div');
        d.textContent = text;
        return d.innerHTML;
      }

      _renderCollapsedOrFull() {
        this._source.innerHTML = this._originalHtml;
        const width = this._source.offsetWidth;
        if (width < 40) {
          return;
        }

        const plain = this._getPlainText();
        if (!plain.length) {
          return;
        }

        if (!this._needsCollapse(width, plain, this._lineLimit)) {
          this.setAttribute('aria-expanded', 'true');
          return;
        }

        const cut = this._findCutIndex(width, plain, this._lineLimit);
        const visible = plain.slice(0, cut).trimEnd();

        this._source.innerHTML =
          '<span class="collection-inline-read-more__text">' +
          this._escapeHtml(visible) +
          '</span>' +
          '<span class="collection-inline-read-more__ellipsis" aria-hidden="true">… </span>' +
          '<button type="button" class="collection-inline-read-more__toggle" aria-expanded="false">' +
          '<strong class="collection-inline-read-more__label">' +
          this._escapeHtml(this._showMore) +
          '</strong></button>';

        const btn = this._source.querySelector('.collection-inline-read-more__toggle');
        if (btn) {
          btn.addEventListener('click', () => this._expand());
        }
        this.setAttribute('aria-expanded', 'false');
      }

      _expand() {
        this._expanded = true;
        this._renderExpanded();
        this.setAttribute('aria-expanded', 'true');
      }

      _collapse() {
        this._expanded = false;
        this._renderCollapsedOrFull();
      }

      _renderExpanded() {
        this._source.innerHTML = this._originalHtml;
        const wrap = document.createElement('p');
        wrap.className = 'collection-inline-read-more__less-wrap';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'collection-inline-read-more__toggle collection-inline-read-more__toggle--less';
        btn.setAttribute('aria-expanded', 'true');
        btn.innerHTML =
          '<strong class="collection-inline-read-more__label">' +
          this._escapeHtml(this._showLess) +
          '</strong>';
        btn.addEventListener('click', () => this._collapse());
        wrap.appendChild(btn);
        this._source.appendChild(wrap);
      }
    }
  );
}
